import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ExternalLink } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

export default function ModerationTab({ projectId, onRefresh }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/moderation/submissions", { params: { project_id: projectId, status: statusFilter } });
      setItems(data);
    } catch { toast.error("Failed to load submissions"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, statusFilter]);

  async function approve(id) {
    setBusyId(id);
    try {
      await api.post(`/moderation/submissions/${id}/approve`);
      toast.success("Approved & published to WordPress");
      load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  }

  async function reject(id) {
    setBusyId(id);
    try {
      await api.post(`/moderation/submissions/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-[10px] txt-mono uppercase text-muted-foreground">Status</span>
        <select
          data-testid={TESTIDS.moderationStatusFilter}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-background border border-border focus:border-primary outline-none px-3 py-1.5 text-xs rounded-sm txt-mono"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <section className="border border-border overflow-x-auto">
        <table className="w-full text-xs" data-testid={TESTIDS.moderationTable}>
          <thead>
            <tr className="bg-secondary border-b border-border text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Type</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">From</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Thread / Title</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Content</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Submitted</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-border last:border-b-0 align-top">
                <td className="px-3 py-2 txt-mono">{it.submission_type === "reply" ? "Reply" : "New question"}</td>
                <td className="px-3 py-2 txt-mono">{it.author_name || "Homeowner"}</td>
                <td className="px-3 py-2 txt-mono max-w-xs">
                  {it.thread_link ? (
                    <a href={it.thread_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      {it.thread_title || it.title || "Thread"} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (it.title || it.thread_title || "—")}
                  {it.service && <div className="text-muted-foreground">{it.service}{it.city ? ` · ${it.city}, ${it.state}` : ""}</div>}
                </td>
                <td className="px-3 py-2 text-muted-foreground max-w-md">{it.content_text}</td>
                <td className="px-3 py-2 txt-mono text-muted-foreground whitespace-nowrap">{new Date(it.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {it.status === "pending" ? (
                    <>
                      <button
                        data-testid={TESTIDS.moderationApproveBtn}
                        onClick={() => approve(it.id)} disabled={busyId === it.id}
                        className="text-green-500 hover:text-green-400 mr-2 disabled:opacity-40" title="Approve & publish"
                      >
                        <Check className="w-4 h-4 inline" />
                      </button>
                      <button
                        data-testid={TESTIDS.moderationRejectBtn}
                        onClick={() => reject(it.id)} disabled={busyId === it.id}
                        className="text-red-500 hover:text-red-400 disabled:opacity-40" title="Reject"
                      >
                        <X className="w-4 h-4 inline" />
                      </button>
                    </>
                  ) : (
                    <span className="text-[10px] txt-mono uppercase text-muted-foreground">{it.status}</span>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-xs">No {statusFilter} submissions.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
